import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import ChunkReport from '../../blocks/chunk-report';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import Elevation from '../../themed/instruction-modals/elevation';
import CustomCertificateForm, { ICertificate } from '../custom-general/custom-certificates/form';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import FviStructureReport from './fviStructureReport';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import Switch from '../../themed/switch';
import StructureItems from './structure-items';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getCurrentUser } from '../../../services/authService';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';

import { matchPhrases, reArrangeInspectionItemAnomalies, reArrangeGeneralPhotos, validateWorkOrderNumber, reArrangePhotoRecord } from '../../../utils/utilities';
import { DEFAULT_REPORT_TITLE, MINIMUM_ITEM_SIZE_FOR_CHUNK_NOTICE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { AnomalyLocationData, ChecklistItem, FVIPDOChecklistItem, FVIStructureItem, IncludeSummary, PDFImageProps, PIDData, User } from '../../../utils/models';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useIncompleteInspectedSections from '../../../hooks/useIncompleteInspectedSections';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomFVIStructureReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [certificates, setCertificates] = useState<ICertificate>();
	const [pdoChecklistItems, setPDOChecklistItems] = useState<FVIPDOChecklistItem[]>(PDO_CHECKLIST);
	const [structureItems, setStructureItems] = useState<FVIStructureItem[]>([]);
	const [includeSummary, setIncludeSummary] = useState<IncludeSummary>('include');
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [anomalyLocationData, setAnomalyLocationData] = useState<AnomalyLocationData>({ hasAnomalyLocations: 'yes', anomalyLocations: [] });
	const [additionalComment, setAdditionalComment] = useState('');
	const [generalPhotos, setGeneralPhotos] = useState<PDFImageProps[]>([]);
	const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(initialChecklistItems);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [hasPDO, setHasPDO] = useState('no');

	const [isChunkNoticeVisible, setChunkNoticeVisible] = useState(false);
	const [itemsToPublish, setItemsToPublish] = useState<FVIStructureItem[]>([]);

	const dispatch = useAppDispatch();

	const { role } = getCurrentUser() as User;
	const { isLastInspectedItemComplete } = useIncompleteInspectedSections(structureItems)

	const getRiskRatingLabel = (): string => {
		const suffix: string = config.enableRiskRating ? 'Enabled' : 'Disabled';
		return 'Risk Breakdown ' + suffix;
	};

	const handleItemsChange = (items: FVIStructureItem[]) => {
		setStructureItems(items);
		autoSave.saveReport();
	};

	const reportDetails = useMemo(() => {
			return {
				additionalComment,
				certificates,
				coverData,
				checklistItems,
				executiveSummary,
				includeSummary,
				hasPDO,
				pidData,
				anomalyLocationData,
				pdoChecklistItems,
				projectDetails,
				reportTitle,
				structureItems,
				generalPhotos,
			};
	}, [projectDetails, certificates, coverData, checklistItems, additionalComment, anomalyLocationData, hasPDO, pidData, pdoChecklistItems, executiveSummary, generalPhotos, structureItems, includeSummary, reportTitle]);

	const validateReportCompletion = useCallback(() => {
		const isPDOChecklistComplete = pdoChecklistItems.every((checklist) => checklist.value);

		if (!isPDOChecklistComplete) {
			return 'Please complete the PDO checklist!';
		}

		if (!validateWorkOrderNumber(coverData.workOrderNumber))
			return 'Enter a valid work order number!';

		if (matchPhrases(pidData.hasPID, 'yes') && pidData.pIDImages.length === 0) {
			return 'Please Upload P & ID images!';
		}

		if (matchPhrases(anomalyLocationData.hasAnomalyLocations, 'yes') && anomalyLocationData.anomalyLocations.length === 0) {
			return 'Please Upload Anomaly Location images!';
		}

		if (!isLastInspectedItemComplete) {
			return 'Please fill all fields first!';
		}

		return '';
	}, [isLastInspectedItemComplete, pdoChecklistItems, pidData, anomalyLocationData,  coverData]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			const validationMessage = validateReportCompletion();
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validateReportCompletion]);

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();

		if (role.approveReports && structureItems.length > MINIMUM_ITEM_SIZE_FOR_CHUNK_NOTICE && itemsToPublish.length === 0) {
			return setChunkNoticeVisible(true);
		}
		
		togglePreviewReport();
	};

	const handleToggleSummary = useCallback(() => {
		setIncludeSummary((previous) => previous === 'include' ? 'exclude' : 'include');
	}, []);

	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		function initializeReport() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

				return;
			}

			const rearrangedAnomalies = reArrangeInspectionItemAnomalies(cachedReport.structureItems) as any[];
			const rearrangedGeneralPhotos = reArrangeGeneralPhotos(rearrangedAnomalies) as any[];

			if (cachedReport.includeSummary) {
				setIncludeSummary(cachedReport.includeSummary);
			}

			if (cachedReport.generalPhotos) {
				const photoRecord = reArrangePhotoRecord(cachedReport.generalPhotos);
				setGeneralPhotos(photoRecord);
				dispatch(setLastPhotoNumber(photoRecord.length));
			}

			if (cachedReport.hasPDO) {
				setHasPDO(cachedReport.hasPDO);
			}

			if (cachedReport.certificates) {
				setCertificates(cachedReport.certificates);
			}

			const pdoChecklist = cachedReport.pdoChecklistItems ?? PDO_CHECKLIST;
			
			setPDOChecklistItems(pdoChecklist);
			setAdditionalComment(cachedReport.additionalComment);
			setChecklistItems(cachedReport.checklistItems);
			setCoverData(cachedReport.coverData.coverData.length > 0 ? cachedReport.coverData : fviCoverData);
			setStructureItems(rearrangedGeneralPhotos);
			setAnomalyLocationData(cachedReport.anomalyLocationData);
			setPIDData(cachedReport.pidData);
			setReportTitle(cachedReport.reportTitle);
			setExecutiveSummary(cachedReport.executiveSummary);
		}

		initializeReport();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.EFVIS));
	}, [dispatch]);

	const handleResetChunkedItemsAndClosePreview = useCallback(() => {
		setItemsToPublish([]);
		togglePreviewReport();
	}, [togglePreviewReport]);
	
	const handleCloseChunkNoticeAndPreviewReport = useCallback(() => {
		setChunkNoticeVisible(false);
		togglePreviewReport();
	}, [togglePreviewReport]);

	return (
		<Fragment>
			{projectDetails && (
				<ChunkReport
					projectId={projectDetails._id}
					projectModule={projectDetails.module}
					reportDetails={reportDetails}
					items={structureItems}
					groupName="structureItems"
					chunkKeyId="structureMemberName" 
					isNoticeVisible={isChunkNoticeVisible}
					onSetChunkedItems={(items) => setItemsToPublish(items)}
					onCloseNotice={() => setChunkNoticeVisible(false)}
					onCancelChunkReport={handleCloseChunkNoticeAndPreviewReport} 
					setFullPreviewVisible={() => setPreviewReport(true)}
				/>
			)}

			<Modal isVisible={previewReport} onClose={handleResetChunkedItemsAndClosePreview}>
				<FviStructureReport {...reportDetails} structureItems={itemsToPublish.length > 0 ? itemsToPublish : structureItems} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilFVIStructure[projectDetails.organisationId]}
					isVisible={projectDetails?.inspectionTypeId === config.supportedTemplates.exxonMobilFVIStructure && role.approveReports}
					subtitle='Update the Cover Data information. This is the default data used on all ExxonMobilFVI Structure Reports'
				/>
			)}

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm
					projectTitle={projectDetails?.projectTitle}
					onAdd={setReportTitle}
				/>
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm
					initialData={coverData}
					onAddCoverForm={setCoverData}
				/>
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<div className="d-flex justify-content-end mt-2">
					<Switch 
						checked={includeSummary === 'include'}
						label='Include Inspection Breakdown'
						onChange={handleToggleSummary} 
					/>
				</div>
				
				<div className="my-4 divider"></div>

				<ExecutiveSummaryForm
					onProceed={setExecutiveSummary}
					initialSummary={executiveSummary}
				/>
			</Zippy>

			<CustomCertificateForm calibrationRequired={false} certificates={certificates} onChangeCertificate={setCertificates} />

			<StructureItems 
				hasPDO={hasPDO}
				onChangeHasPDO={setHasPDO}
				additionalComment={additionalComment}
				checklistLabel="STRUCTURE"
				checklistItems={checklistItems} 
				pdoChecklistItems={pdoChecklistItems}
				onChangePDOChecklistItems={setPDOChecklistItems}
				onChangeAdditionalComment={setAdditionalComment}
				onChangeChecklistItems={setChecklistItems}
				onChangeAnomalyLocationData={setAnomalyLocationData}
				anomalyLocationData={anomalyLocationData}
				generalPhotos={generalPhotos}
				onChangeGeneralPhotos={setGeneralPhotos}
				pidData={pidData}
				onChangePIDData={setPIDData}
				items={structureItems} 
				onItemsChange={handleItemsChange} 
			/>
			
			<div className="d-flex justify-content-end my-4">
				<Switch
					label={getRiskRatingLabel()}
					checked={config.enableRiskRating}
					onChange={() => {}}
					readOnly
				/>
			</div>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialChecklistItems: ChecklistItem[] = [
	{
		id: '1',
		lineNumber: 'Coating condition',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '2',
		lineNumber: 'Corrosion',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '3',
		lineNumber: 'Mechanical Damage',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '4',
		lineNumber: "Vibration",
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '5',
		lineNumber: 'Weld Joint Inspection',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '6',
		lineNumber: 'Air Gap Survey',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '7',
		lineNumber: 'Cathodic Protection (CP) Survey',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '8',
		lineNumber: 'Ladders',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '9',
		lineNumber: 'Stairways',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '10',
		lineNumber: 'Walk ways',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '11',
		lineNumber: 'Handrails',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '12',
		lineNumber: 'Hoses',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '13',
		lineNumber: 'Beams',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
	{
		id: '14',
		lineNumber: 'Bracing',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: '',
	},
];

export const PDO_CHECKLIST: FVIPDOChecklistItem[] = [
	{
		id: '1',
		label: 'Are there any pre-existing dropped object hazards in the work area, such as loose items or debris?',
		value: '',
		category: 'general',
	},
	{
		id: '2',
		label: 'Are all tools properly fastened and adequately secured?',
		value: '',
		category: 'general',
	},
	{
		id: '3',
		label: 'Are equipment, objects, and tools properly secured or tied down to prevent impact or displacement due to windy or inclement weather?',
		value: '',
		category: 'general',
	},
	{
		id: '4',
		label: 'Are there any visible signs of floor gaps, damaged grating, mesh, or handrails?',
		value: '',
		category: 'general',
	},
	{
		id: '5',
		label: 'Are tools and tackles adequately secured while working at height?',
		value: '',
		category: 'general',
	},
	{
		id: '6',
		label: 'Are there any moving machinery, cranes, or vehicles in the travel path that could cause collisions or load shifts?',
		value: '',
		category: 'general',
	},
	{
		id: '7',
		label: 'Is there any poor housekeeping after completion of work?',
		value: '',
		category: 'general',
	},
	{
		id: '8',
		label: 'Have the structural members (beams, columns, ties), especially overhead bolted connections, been checked for heavy corrosion?',
		value: '',
		category: 'components',
	},
	{
		id: '9',
		label: 'Have all fastenings, bolts, clips, covers, panels, hatches, and removable guardrails been checked to ensure they are properly secured?',
		value: '',
		category: 'components',
	},
	{
		id: '10',
		label: 'Have all safety securing devices been checked to ensure they are properly installed and attached to a sound structure?',
		value: '',
		category: 'components',
	},
	{
		id: '11',
		label: 'Have all overhead platforms, monkey ladders, and attachments been inspected for signs of corrosion or paint peeling?',
		value: '',
		category: 'components',
	},
	{
		id: '12',
		label: 'Have all guards, barricades, and toe boards been checked to ensure they are in place, and are all hatches, openings, and gaps properly closed or covered?',
		value: '',
		category: 'components',
	},
	{
		id: '13',
		label: 'Have all lighting, cable trays, brackets, and other fixtures been inspected, with special attention to any corroded items that may be insecure or present a snagging or collision hazard?',
		value: '',
		category: 'components',
	},
	{
		id: '14',
		label: 'Is the grating alignment within 1 inch to prevent trip hazards?',
		value: '',
		category: 'components',
	},
];

export default CustomFVIStructureReport;
